import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, ButtonGroup, Divider, IconButton, Input, Message, Panel, SelectPicker, Stack, Table, Tooltip, Whisper } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import {
  useBulkUpsertTaskOdcActivityMonthly,
  useBulkUpsertTaskOdcMonthly,
  useTask,
  useTaskOdcActivityTotals,
  useTaskOdcActivityMonthlyForMonths,
  useTaskOdcMonthlyForMonths,
  useTaskOdcMonthlyTotals,
} from '../hooks/useQueries';
import usePermissions from '../hooks/usePermissions';

import './ActivityHourForecastPage.css';
import './TaskOdcForecastPage.css';

const WINDOW_SIZE = 6;
const PAST_FORECAST_OVERRIDE_CLICK_THRESHOLD = 5;

const pad2 = (n) => String(n).padStart(2, '0');

const isIsoDate = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

const toMonthStartIso = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-01`;
};

const normalizeMonthStart = (raw) => {
  const s = String(raw || '').slice(0, 10);
  if (!isIsoDate(s)) return null;
  return `${s.slice(0, 7)}-01`;
};

const parseLocalDate = (yyyyMmDd) => {
  const s = String(yyyyMmDd || '').slice(0, 10);
  if (!isIsoDate(s)) return null;
  const y = Number(s.slice(0, 4));
  const m0 = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));
  return new Date(y, m0, d);
};

const addMonths = (monthStartIso, deltaMonths) => {
  const d = parseLocalDate(monthStartIso);
  if (!d) return null;
  const next = new Date(d.getFullYear(), d.getMonth() + Number(deltaMonths || 0), 1);
  return toMonthStartIso(next);
};

const snapToHalfYearStart = (monthStartIso) => {
  const d = parseLocalDate(monthStartIso);
  if (!d) return null;
  const monthIndex = d.getMonth();
  const startMonth = monthIndex < 6 ? 0 : 6;
  return toMonthStartIso(new Date(d.getFullYear(), startMonth, 1));
};

const formatMonthLabel = (monthStartIso) => {
  const d = parseLocalDate(monthStartIso);
  if (!d) return monthStartIso;
  const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = MONTHS_SHORT[d.getMonth()] || '';
  const yy = String(d.getFullYear()).slice(-2);
  return `${month} '${yy}`;
};

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const formatCurrencyInputValue = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const normalizeCurrencyInput = (value) => {
  const raw = String(value || '').replace(/[^\d.]/g, '');
  if (!raw) return '';

  const dotIndex = raw.indexOf('.');
  const hasDot = dotIndex >= 0;
  const integerPartRaw = hasDot ? raw.slice(0, dotIndex) : raw;
  const decimalPartRaw = hasDot ? raw.slice(dotIndex + 1).replace(/\./g, '') : '';

  const integerDigits = integerPartRaw.replace(/^0+(?=\d)/, '') || '0';
  const integerFormatted = integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const decimalDigits = decimalPartRaw.slice(0, 2);

  if (hasDot) {
    return `$${integerFormatted}.${decimalDigits}`;
  }
  return `$${integerFormatted}`;
};

const parseCurrencyInput = (value) => {
  const raw = String(value || '').replace(/[^\d.]/g, '');
  if (!raw || raw === '.') return NaN;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
};

const readStartFromSearchParams = (searchParams) => {
  const raw = String(searchParams?.get?.('start') || '').slice(0, 10);
  const normalized = normalizeMonthStart(raw);
  return normalized;
};

const readItemIdFromSearchParams = (searchParams) => {
  const raw = searchParams?.get?.('itemId') ?? searchParams?.get?.('activityId');
  const n = Number(raw);
  return Number.isInteger(n) ? n : null;
};

const { Column, HeaderCell, Cell } = Table;

export default function TaskOdcForecastPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const taskId = Number(params?.taskId);
  const projectIdParam = Number(params?.projectId);
  const taskOrderIdParam = Number(params?.taskOrderId);

  const [start, setStart] = useState(() => {
    const initial = readStartFromSearchParams(searchParams) ?? toMonthStartIso(new Date());
    return snapToHalfYearStart(initial) ?? initial;
  });

  const { data: task, isLoading: taskLoading } = useTask(Number.isInteger(taskId) ? taskId : null);

  // ODC amounts are saved as part of the forecast, so editing them needs Forecast hours.
  const permissions = usePermissions();
  const canForecast = permissions.canProject(Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : projectIdParam, 'disciplines');

  const monthStarts = useMemo(() => {
    const out = [];
    for (let i = 0; i < WINDOW_SIZE; i += 1) {
      const ms = addMonths(start, i);
      if (ms) out.push(ms);
    }
    return out;
  }, [start]);

  const { data, isLoading, isFetching, error } = useTaskOdcMonthlyForMonths(
    Number.isInteger(taskId) ? taskId : null,
    monthStarts,
    { enabled: Number.isInteger(taskId) && monthStarts.length > 0 }
  );

  const {
    data: activityBreakdownData,
    isLoading: activityBreakdownLoading,
    error: activityBreakdownError,
  } = useTaskOdcActivityMonthlyForMonths(
    Number.isInteger(taskId) ? taskId : null,
    monthStarts,
    { enabled: Number.isInteger(taskId) && monthStarts.length > 0 }
  );

  const bulkUpsert = useBulkUpsertTaskOdcMonthly();
  const bulkUpsertActivity = useBulkUpsertTaskOdcActivityMonthly();
  const { data: totalsData } = useTaskOdcMonthlyTotals(Number.isInteger(taskId) ? taskId : null, {
    enabled: Number.isInteger(taskId),
  });

  const { data: odcActivityTotalsData } = useTaskOdcActivityTotals(Number.isInteger(taskId) ? taskId : null, {
    enabled: Number.isInteger(taskId),
  });

  const currentMonthStartIso = useMemo(() => normalizeMonthStart(toMonthStartIso(new Date())), []);
  const [pastForecastOverrideEnabled, setPastForecastOverrideEnabled] = useState(false);
  const pastForecastUnlockAttemptCountRef = useRef(0);
  const [pastForecastOverrideModalOpen, setPastForecastOverrideModalOpen] = useState(false);

  const draftRef = useRef({});
  const draftInputRef = useRef({});
  const [draftVersion, setDraftVersion] = useState(0);

  const activityDraftRef = useRef({});
  const activityDraftInputRef = useRef({});
  const [activityDraftVersion, setActivityDraftVersion] = useState(0);
  const [selectedActivityId, setSelectedActivityId] = useState(() => readItemIdFromSearchParams(searchParams));

  const rowsFromApi = useMemo(() => (Array.isArray(data?.rows) ? data.rows : []), [data?.rows]);

  const rowByMonth = useMemo(() => {
    const out = new Map();
    for (const row of rowsFromApi) {
      const ms = normalizeMonthStart(row?.monthStart);
      if (!ms) continue;
      out.set(ms, {
        monthStart: ms,
        forecastAmount: Number(row?.forecastAmount ?? 0),
        actualAmount: Number(row?.actualAmount ?? 0),
        actualLocked: row?.actualLocked === true,
        actualSource: String(row?.actualSource || 'manual'),
      });
    }
    return out;
  }, [rowsFromApi]);

  const isActualLockedByInvoice = (monthStart) => {
    const row = rowByMonth.get(monthStart);
    return row?.actualLocked === true || String(row?.actualSource || '').toLowerCase() === 'invoice';
  };

  const getBaseValue = (monthStart, field) => {
    const row = rowByMonth.get(monthStart);
    return Number(row?.[field] ?? 0);
  };

  const activityForecastByKey = useMemo(() => {
    const out = new Map();
    const activities = Array.isArray(activityBreakdownData?.activities) ? activityBreakdownData.activities : [];
    for (const activity of activities) {
      const activityId = Number(activity?.activityId);
      if (!Number.isInteger(activityId)) continue;
      const rows = Array.isArray(activity?.rows) ? activity.rows : [];
      for (const row of rows) {
        const monthStart = normalizeMonthStart(row?.monthStart);
        if (!monthStart) continue;
        out.set(`${activityId}|${monthStart}`, Number(row?.forecastAmount ?? 0));
      }
    }
    return out;
  }, [activityBreakdownData?.activities]);

  const activityRows = useMemo(() => {
    const byId = new Map();

    const taskActivities = Array.isArray(task?.activities) ? task.activities : [];
    for (const a of taskActivities) {
      const activityId = Number(a?.id ?? a?.activityId);
      if (!Number.isInteger(activityId)) continue;
      const activityName = String(a?.name ?? a?.activityName ?? `Item #${activityId}`);
      byId.set(activityId, activityName);
    }

    const apiActivities = Array.isArray(activityBreakdownData?.activities) ? activityBreakdownData.activities : [];
    for (const a of apiActivities) {
      const activityId = Number(a?.activityId ?? a?.id);
      if (!Number.isInteger(activityId)) continue;
      const activityName = String(a?.activityName ?? a?.name ?? `Item #${activityId}`);
      if (!byId.has(activityId)) byId.set(activityId, activityName);
    }

    return Array.from(byId.entries())
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([activityId, activityName], index) => ({
        activityId,
        activityName,
        activityNumber: index + 1,
      }));
  }, [activityBreakdownData?.activities, task?.activities]);

  const activityOptions = useMemo(
    () => activityRows.map((a) => ({
      label: `Item: #${Number(a?.activityNumber)} ${String(a?.activityName || 'Item')}`,
      value: Number(a?.activityId),
    })),
    [activityRows]
  );

  const selectedActivityName = useMemo(() => {
    const selectedId = Number(selectedActivityId);
    if (!Number.isInteger(selectedId)) return null;
    const match = activityRows.find((a) => Number(a?.activityId) === selectedId);
    return match?.activityName ? String(match.activityName) : null;
  }, [activityRows, selectedActivityId]);

  const selectedActivityTotals = useMemo(() => {
    const selectedId = Number(selectedActivityId);
    if (!Number.isInteger(selectedId)) return null;

    const rows = Array.isArray(odcActivityTotalsData?.rows) ? odcActivityTotalsData.rows : [];
    const match = rows.find((r) => Number(r?.activityId) === selectedId);
    if (!match) return null;

    return {
      forecastAmount: Number(match?.forecastAmount ?? 0),
      actualAmount: Number(match?.actualAmount ?? 0),
    };
  }, [odcActivityTotalsData?.rows, selectedActivityId]);

  const odcTableRows = useMemo(
    () => [
      {
        id: 'forecast',
        rowType: 'forecast',
        label: selectedActivityName ? `Forecasted ${selectedActivityName}` : 'Forecasted ODC',
      },
      {
        id: 'actual',
        rowType: 'actual',
        label: selectedActivityName ? `Actual ${selectedActivityName}` : 'Actual ODC',
      },
    ],
    [selectedActivityName]
  );

  const setSelectedActivityAndSyncSearch = useCallback((value) => {
    const nextId = Number.isInteger(Number(value)) ? Number(value) : null;
    setSelectedActivityId(nextId);

    const currentId = readItemIdFromSearchParams(searchParams);
    if (currentId === nextId) return;

    const next = new URLSearchParams(searchParams);
    if (Number.isInteger(nextId)) next.set('itemId', String(nextId));
    else next.delete('itemId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (activityRows.length === 0) {
      setSelectedActivityAndSyncSearch(null);
      return;
    }

    const current = Number(selectedActivityId);
    const exists = activityRows.some((a) => Number(a?.activityId) === current);
    if (!exists) {
      setSelectedActivityAndSyncSearch(Number(activityRows[0]?.activityId));
    }
  }, [activityRows, selectedActivityId, setSelectedActivityAndSyncSearch]);

  const getActivityKey = (activityId, monthStart) => `${activityId}|${monthStart}`;

  const getActivityBaseValue = (activityId, monthStart) => {
    const key = getActivityKey(activityId, monthStart);
    return Number(activityForecastByKey.get(key) ?? 0);
  };

  const getActivityDisplayValue = (activityId, monthStart) => {
    const key = getActivityKey(activityId, monthStart);
    if (Object.prototype.hasOwnProperty.call(activityDraftRef.current, key)) {
      return activityDraftRef.current[key];
    }
    return getActivityBaseValue(activityId, monthStart);
  };

  const getActivityDisplayInputValue = (activityId, monthStart) => {
    const key = getActivityKey(activityId, monthStart);
    if (Object.prototype.hasOwnProperty.call(activityDraftInputRef.current, key)) {
      return String(activityDraftInputRef.current[key] ?? '');
    }
    return formatCurrencyInputValue(getActivityDisplayValue(activityId, monthStart));
  };

  const onChangeActivityInputValue = (activityId, monthStart, rawValue) => {
    if (isPastMonthForecastLocked(monthStart)) return;
    const key = getActivityKey(activityId, monthStart);
    const liveFormatted = normalizeCurrencyInput(rawValue);
    activityDraftInputRef.current[key] = liveFormatted;

    const parsed = parseCurrencyInput(liveFormatted);
    if (!liveFormatted || !Number.isFinite(parsed)) {
      activityDraftRef.current[key] = 0;
      setActivityDraftVersion((v) => v + 1);
      return;
    }

    activityDraftRef.current[key] = Math.max(0, parsed);
    setActivityDraftVersion((v) => v + 1);
  };

  const onBlurActivityInputValue = (activityId, monthStart) => {
    const key = getActivityKey(activityId, monthStart);
    const raw = String(activityDraftInputRef.current[key] ?? '').replace(/[$,\s]/g, '').trim();

    if (raw === '' || raw === '.') {
      activityDraftRef.current[key] = 0;
      activityDraftInputRef.current[key] = formatCurrencyInputValue(0);
      setActivityDraftVersion((v) => v + 1);
      return;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      activityDraftInputRef.current[key] = formatCurrencyInputValue(getActivityBaseValue(activityId, monthStart));
      setActivityDraftVersion((v) => v + 1);
      return;
    }

    const normalized = Math.round(parsed * 100) / 100;
    activityDraftRef.current[key] = normalized;
    activityDraftInputRef.current[key] = formatCurrencyInputValue(normalized);
    setActivityDraftVersion((v) => v + 1);
  };

  const getDraftKey = (monthStart, field) => `${monthStart}|${field}`;

  const getDisplayValue = (monthStart, field) => {
    if (field === 'actualAmount' && isActualLockedByInvoice(monthStart)) {
      return getBaseValue(monthStart, field);
    }

    const key = getDraftKey(monthStart, field);
    if (Object.prototype.hasOwnProperty.call(draftRef.current, key)) {
      return draftRef.current[key];
    }
    return getBaseValue(monthStart, field);
  };

  const isMonthFullyInPast = (monthStartIso) => {
    const normalized = normalizeMonthStart(monthStartIso);
    if (!normalized || !currentMonthStartIso) return false;
    return normalized < currentMonthStartIso;
  };

  const isPastMonthForecastLocked = (monthStartIso) => isMonthFullyInPast(monthStartIso) && !pastForecastOverrideEnabled;

  const registerPastForecastUnlockAttempt = () => {
    if (pastForecastOverrideEnabled) return;
    const next = Number(pastForecastUnlockAttemptCountRef.current || 0) + 1;
    if (next >= PAST_FORECAST_OVERRIDE_CLICK_THRESHOLD) {
      pastForecastUnlockAttemptCountRef.current = 0;
      setPastForecastOverrideModalOpen(true);
      return;
    }
    pastForecastUnlockAttemptCountRef.current = next;
  };

  const hasTaskUnsavedChanges = useMemo(() => {
    for (const monthStart of monthStarts) {
      const baseActual = getBaseValue(monthStart, 'actualAmount');
      const nextActual = isActualLockedByInvoice(monthStart)
        ? baseActual
        : Number(getDisplayValue(monthStart, 'actualAmount') ?? 0);
      if (nextActual !== baseActual) return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftVersion, monthStarts, rowByMonth]);

  const taskDirtyEdits = useMemo(() => {
    const edits = [];
    for (const monthStart of monthStarts) {
      const baseForecast = getBaseValue(monthStart, 'forecastAmount');
      const baseActual = getBaseValue(monthStart, 'actualAmount');

      const nextActual = isActualLockedByInvoice(monthStart)
        ? baseActual
        : Number(getDisplayValue(monthStart, 'actualAmount') ?? 0);

      if (nextActual === baseActual) continue;
      edits.push({ monthStart, forecastAmount: baseForecast, actualAmount: nextActual });
    }
    return edits;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftVersion, monthStarts, rowByMonth]);

  const hasActivityUnsavedChanges = useMemo(() => {
    for (const activity of activityRows) {
      const activityId = Number(activity?.activityId);
      if (!Number.isInteger(activityId)) continue;
      for (const monthStart of monthStarts) {
        const base = Number(getActivityBaseValue(activityId, monthStart) ?? 0);
        const next = Number(getActivityDisplayValue(activityId, monthStart) ?? 0);
        if (next !== base) return true;
      }
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityDraftVersion, activityRows, monthStarts, activityForecastByKey]);

  const activityDirtyEdits = useMemo(() => {
    const edits = [];
    for (const activity of activityRows) {
      const activityId = Number(activity?.activityId);
      if (!Number.isInteger(activityId)) continue;
      for (const monthStart of monthStarts) {
        const base = Number(getActivityBaseValue(activityId, monthStart) ?? 0);
        const next = Number(getActivityDisplayValue(activityId, monthStart) ?? 0);
        if (next === base) continue;
        edits.push({
          activityId,
          monthStart,
          forecastAmount: next,
        });
      }
    }
    return edits;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityDraftVersion, activityRows, monthStarts, activityForecastByKey]);

  const hasAnyUnsavedChanges = hasTaskUnsavedChanges || hasActivityUnsavedChanges;

  const getDisplayInputValue = (monthStart, field) => {
    if (field === 'actualAmount' && isActualLockedByInvoice(monthStart)) {
      return formatCurrencyInputValue(getBaseValue(monthStart, field));
    }

    const key = getDraftKey(monthStart, field);
    if (Object.prototype.hasOwnProperty.call(draftInputRef.current, key)) {
      return String(draftInputRef.current[key] ?? '');
    }
    return formatCurrencyInputValue(getDisplayValue(monthStart, field));
  };

  const onChangeInputValue = (monthStart, field, rawValue) => {
    if (field === 'actualAmount' && isActualLockedByInvoice(monthStart)) {
      return;
    }

    const key = getDraftKey(monthStart, field);
    const liveFormatted = normalizeCurrencyInput(rawValue);
    draftInputRef.current[key] = liveFormatted;

    const parsed = parseCurrencyInput(liveFormatted);
    if (!liveFormatted || !Number.isFinite(parsed)) {
      draftRef.current[key] = 0;
      setDraftVersion((v) => v + 1);
      return;
    }

    draftRef.current[key] = Math.max(0, parsed);
    setDraftVersion((v) => v + 1);
  };

  const onBlurInputValue = (monthStart, field) => {
    const key = getDraftKey(monthStart, field);

    if (field === 'actualAmount' && isActualLockedByInvoice(monthStart)) {
      const base = getBaseValue(monthStart, field);
      delete draftRef.current[key];
      delete draftInputRef.current[key];
      draftInputRef.current[key] = formatCurrencyInputValue(base);
      setDraftVersion((v) => v + 1);
      return;
    }

    const raw = String(draftInputRef.current[key] ?? '').replace(/[$,\s]/g, '').trim();

    if (raw === '' || raw === '.') {
      draftRef.current[key] = 0;
      draftInputRef.current[key] = formatCurrencyInputValue(0);
      setDraftVersion((v) => v + 1);
      return;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      draftInputRef.current[key] = formatCurrencyInputValue(getDisplayValue(monthStart, field));
      setDraftVersion((v) => v + 1);
      return;
    }

    const normalized = Math.round(parsed * 100) / 100;
    draftRef.current[key] = normalized;
    draftInputRef.current[key] = formatCurrencyInputValue(normalized);
    setDraftVersion((v) => v + 1);
  };

  const clearDrafts = () => {
    draftRef.current = {};
    draftInputRef.current = {};
    activityDraftRef.current = {};
    activityDraftInputRef.current = {};
    setDraftVersion((v) => v + 1);
    setActivityDraftVersion((v) => v + 1);
  };

  const confirmDiscard = () => {
    if (!hasAnyUnsavedChanges) return true;
    const ok = window.confirm('You have unsaved changes. Discard them and continue?');
    if (ok) clearDrafts();
    return ok;
  };

  const goWindow = (delta) => {
    const ns = addMonths(start, delta * WINDOW_SIZE);
    if (!ns) return;
    const snapped = snapToHalfYearStart(ns) ?? ns;
    if (!confirmDiscard()) return;
    setStart(snapped);
    const next = new URLSearchParams(searchParams);
    next.set('start', snapped);
    setSearchParams(next, { replace: true });
  };

  const goCurrent = () => {
    const ns = snapToHalfYearStart(toMonthStartIso(new Date()));
    if (!ns) return;
    if (!confirmDiscard()) return;
    setStart(ns);
    const next = new URLSearchParams(searchParams);
    next.set('start', ns);
    setSearchParams(next, { replace: true });
  };

  const saveAll = async () => {
    if (!Number.isInteger(taskId)) return;
    if (bulkUpsert.isPending || bulkUpsertActivity.isPending) return;
    if (taskDirtyEdits.length === 0 && activityDirtyEdits.length === 0) return;

    setPastForecastOverrideEnabled(false);
    pastForecastUnlockAttemptCountRef.current = 0;
    setPastForecastOverrideModalOpen(false);

    if (taskDirtyEdits.length > 0) {
      await bulkUpsert.mutateAsync({ taskId, edits: taskDirtyEdits });
    }

    if (activityDirtyEdits.length > 0) {
      await bulkUpsertActivity.mutateAsync({ taskId, edits: activityDirtyEdits });
    }

    clearDrafts();
  };

  const allTimeTotals = useMemo(
    () => ({
      forecast: Number(totalsData?.forecastAmount ?? 0),
      actual: Number(totalsData?.actualAmount ?? 0),
    }),
    [totalsData?.actualAmount, totalsData?.forecastAmount]
  );

  const isSavingAny = bulkUpsert.isPending || bulkUpsertActivity.isPending;

  const taskType = String(task?.taskType || '').toLowerCase();

  const title = task?.projectName ? `ODC Forecast - Project: ${task.projectName}` : 'ODC Forecast';

  return (
    <div className="ppc-page">
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <h3 style={{ margin: 0, lineHeight: '22px' }}>{title}</h3>
        <ButtonGroup>
          {hasAnyUnsavedChanges && canForecast ? (
            <Button appearance="primary" loading={isSavingAny} disabled={isSavingAny} onClick={saveAll}>
              Save
            </Button>
          ) : null}
          <Button
            appearance="ghost"
            onClick={() => {
              if (!confirmDiscard()) return;
              if (Number.isInteger(projectIdParam) && Number.isInteger(taskOrderIdParam) && Number.isInteger(taskId)) {
                navigate(`/projects/${projectIdParam}/task-orders/${taskOrderIdParam}/tasks/${taskId}`);
                return;
              }
              if (Number.isInteger(taskId)) {
                navigate(`/tasks/${taskId}`);
                return;
              }
              navigate('/');
            }}
          >
            Back
          </Button>
        </ButtonGroup>
      </Stack>

      <Divider />

      {error ? (
        <Message type="error" showIcon>
          Failed to load ODC monthly values.
        </Message>
      ) : null}

      {!taskLoading && taskType && taskType !== 'odc' ? (
        <Message type="warning" showIcon>
          This task is not ODC. Use Forecast Hours instead.
        </Message>
      ) : null}

      <Panel
        className="ppc-odc-forecast-panel"
        bordered
        header={
          <div className="ppc-forecast-header">
            <div className="ppc-forecast-header-center">
              <div className="ppc-activity-picker-row">
                <SelectPicker
                  cleanable={false}
                  searchable={activityOptions.length > 7}
                  className="ppc-activity-picker"
                  menuClassName="ppc-activity-picker-popup"
                  data={activityOptions}
                  value={selectedActivityId}
                  onChange={setSelectedActivityAndSyncSearch}
                  disabled={activityRows.length === 0 || isSavingAny}
                  placeholder={activityRows.length === 0 ? 'No ODC items' : 'Select ODC item'}
                />
              </div>
            </div>

            <div className="ppc-forecast-header-right ppc-forecast-range">
              <div className="ppc-forecast-range-row">
                <IconButton
                  appearance="subtle"
                  size="sm"
                  icon={<i className="bi bi-chevron-left" aria-hidden="true" />}
                  aria-label="Previous months"
                  onClick={() => goWindow(-1)}
                />
                <span className="ppc-forecast-range-label">
                  {formatMonthLabel(monthStarts[0])} – {formatMonthLabel(monthStarts[monthStarts.length - 1])}
                </span>
                <IconButton
                  appearance="subtle"
                  size="sm"
                  icon={<i className="bi bi-chevron-right" aria-hidden="true" />}
                  aria-label="Next months"
                  onClick={() => goWindow(1)}
                />
                <Button appearance="subtle" size="sm" onClick={goCurrent}>
                  Current
                </Button>
              </div>
            </div>
          </div>
        }
      >
        {activityRows.length === 0 ? (
          <Message type="info" showIcon style={{ marginBottom: 10 }}>
            No ODC items found for this task.
          </Message>
        ) : null}

        <div className="ppc-odc-forecast-table-wrap">
          <Table
            className="ppc-odc-forecast-table"
            data={odcTableRows}
            autoHeight
            bordered
            cellBordered
            rowKey={(row) => String(row?.id)}
            rowHeight={66}
            headerHeight={38}
            style={{ width: '100%' }}
          >
            <Column width={170} fixed>
              <HeaderCell>Type</HeaderCell>
              <Cell>
                {(rowData) => (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                    {rowData?.label || '—'}
                  </div>
                )}
              </Cell>
            </Column>

            {monthStarts.map((monthStart) => (
              <Column key={`odc-month-${monthStart}`} width={170} flexGrow={1} align="right">
                <HeaderCell>{formatMonthLabel(monthStart)}</HeaderCell>
                <Cell>
                  {(rowData) => {
                    const rowType = String(rowData?.rowType || '');
                    const selectedId = Number(selectedActivityId);

                    if (rowType === 'forecast') {
                      const forecastLocked = isPastMonthForecastLocked(monthStart);
                      return (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <div style={{ position: 'relative', width: 120 }}>
                            <Input
                              size="sm"
                              className="ppc-odc-forecast-input"
                              value={
                                Number.isInteger(selectedId)
                                  ? getActivityDisplayInputValue(selectedId, monthStart)
                                  : formatCurrencyInputValue(0)
                              }
                              onChange={(v) => {
                                if (!Number.isInteger(selectedId)) return;
                                onChangeActivityInputValue(selectedId, monthStart, v);
                              }}
                              onBlur={() => {
                                if (!Number.isInteger(selectedId)) return;
                                onBlurActivityInputValue(selectedId, monthStart);
                              }}
                              onFocus={(e) => queueMicrotask(() => e?.target?.select?.())}
                              onClick={(e) => queueMicrotask(() => e?.target?.select?.())}
                              disabled={!canForecast || isSavingAny || forecastLocked || !Number.isInteger(selectedId)}
                              style={{ width: '100%' }}
                              inputMode="decimal"
                            />
                            {forecastLocked ? (
                              <Whisper trigger="hover" placement="top" speaker={<Tooltip>Cannot edit past forecasts</Tooltip>}>
                                <button
                                  type="button"
                                  aria-label="Cannot edit past forecast months"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    registerPastForecastUnlockAttempt();
                                  }}
                                  style={{
                                    position: 'absolute',
                                    inset: 0,
                                    border: 'none',
                                    background: 'transparent',
                                    padding: 0,
                                    margin: 0,
                                    cursor: 'not-allowed',
                                  }}
                                />
                              </Whisper>
                            ) : null}
                          </div>
                        </div>
                      );
                    }

                    const actualLocked = isActualLockedByInvoice(monthStart);
                    const inputNode = (
                      <Input
                        size="sm"
                        className="ppc-odc-forecast-input"
                        value={getDisplayInputValue(monthStart, 'actualAmount')}
                        onChange={(v) => onChangeInputValue(monthStart, 'actualAmount', v)}
                        onBlur={() => onBlurInputValue(monthStart, 'actualAmount')}
                        onFocus={(e) => queueMicrotask(() => e?.target?.select?.())}
                        onClick={(e) => queueMicrotask(() => e?.target?.select?.())}
                        disabled={!canForecast || isSavingAny || isLoading || isFetching || actualLocked}
                        style={{ width: 120 }}
                        inputMode="decimal"
                      />
                    );

                    return (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {!actualLocked ? (
                          inputNode
                        ) : (
                          <Whisper
                            trigger="hover"
                            placement="top"
                            speaker={
                              <Tooltip>
                                This amount was pulled directly from the invoice, it is not editable
                              </Tooltip>
                            }
                          >
                            <span style={{ display: 'inline-flex', width: 120 }}>{inputNode}</span>
                          </Whisper>
                        )}
                      </div>
                    );
                  }}
                </Cell>
              </Column>
            ))}

            <Column width={120} fixed="right" align="right">
              <HeaderCell>Total ($)</HeaderCell>
              <Cell>
                {(rowData) => {
                  const rowType = String(rowData?.rowType || '');
                  if (rowType === 'forecast') {
                    const selectedId = Number(selectedActivityId);
                    if (Number.isInteger(selectedId) && selectedActivityTotals) {
                      return (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                            {formatCurrency(Number(selectedActivityTotals.forecastAmount ?? 0))}
                          </span>
                        </div>
                      );
                    }

                    const windowTotal = monthStarts.reduce((sum, ms) => {
                      if (!Number.isInteger(selectedId)) return sum;
                      return sum + Number(getActivityDisplayValue(selectedId, ms) ?? 0);
                    }, 0);
                    return (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                          {formatCurrency(windowTotal)}
                        </span>
                      </div>
                    );
                  }

                  const selectedId = Number(selectedActivityId);
                  if (Number.isInteger(selectedId) && selectedActivityTotals) {
                    return (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                          {formatCurrency(Number(selectedActivityTotals.actualAmount ?? 0))}
                        </span>
                      </div>
                    );
                  }

                  if (Number.isInteger(selectedId) && !selectedActivityTotals) {
                    return (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#666' }}>
                          —
                        </span>
                      </div>
                    );
                  }

                  return (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                        {formatCurrency(Number(allTimeTotals.actual ?? 0))}
                      </span>
                    </div>
                  );
                }}
              </Cell>
            </Column>
          </Table>
        </div>

        {activityBreakdownError ? (
          <Message type="error" showIcon style={{ marginTop: 10 }}>
            Failed to load ODC item forecast breakdown.
          </Message>
        ) : null}

        {activityBreakdownLoading ? (
          <div style={{ color: '#666', fontSize: 12, marginTop: 8 }}>Loading ODC item forecast…</div>
        ) : null}

        <div className="ppc-odc-forecast-total-row">
          <div>
            <div className="ppc-odc-forecast-total-label">Project Forecast Total</div>
            <div className="ppc-odc-forecast-total-value">{formatCurrency(allTimeTotals.forecast)}</div>
          </div>
          <div>
            <div className="ppc-odc-forecast-total-label">Project Actual Total</div>
            <div className="ppc-odc-forecast-total-value">{formatCurrency(allTimeTotals.actual)}</div>
          </div>
        </div>
      </Panel>

      <Modal open={pastForecastOverrideModalOpen} onClose={() => setPastForecastOverrideModalOpen(false)} size="xs">
        <Modal.Header>
          <Modal.Title>Override past forecast lock?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            Are you sure you want to override past forecast locking for this session?
          </Message>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setPastForecastOverrideModalOpen(false)}>
            Close
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            onClick={() => {
              setPastForecastOverrideEnabled(true);
              pastForecastUnlockAttemptCountRef.current = 0;
              setPastForecastOverrideModalOpen(false);
            }}
          >
            Override
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
